import { FeedbackSentiment } from '../feedback/sentimentClassifier';
import { DimensionType, Format, MetricType } from '../types/field';
/**
 * Rating thresholds on the rating's normalised position in its own scale,
 * `(value - min) / (max - min)`, so any scale works. Chosen to match NPS on
 * 0..10: 9-10 promoters, 7-8 passives, 0-6 detractors. On a 1..5 star scale this
 * makes 5 positive, 4 neutral, 1-3 negative (top-box CSAT). Carried over
 * unchanged from `feedback/feedbackRollup.ts`, which classified in Node.
 */
const POSITIVE_POSITION_THRESHOLD = 0.9;
const NEGATIVE_POSITION_THRESHOLD = 0.6;
/** Net sentiment is reported on -100..100, like NPS, not as a 0..1 ratio. */
const NET_SENTIMENT_SCALE = 100;
/**
 * A rating's position within its own scale. Composed once and reused by every
 * rating-derived field so the normalisation lives in exactly one place.
 * NULLIF guards a degenerate scale where min equals max.
 */
const RATING_POSITION = `(\${col.rating} - \${col.rating_scale_min}) / NULLIF(\${col.rating_scale_max} - \${col.rating_scale_min}, 0)`;
/** Counts source-provided sentiment labels case-insensitively. */
const sentimentCountSql = (sentiment) => `CASE WHEN LOWER(\${col.sentiment}) = '${sentiment}' THEN 1 ELSE 0 END`;
/** Counts rows whose landed priority equals `priority`, case-insensitively. */
const priorityCountSql = (priority) => `CASE WHEN LOWER(\${col.priority}) = '${priority}' THEN 1 ELSE 0 END`;
/**
 * Same taxonomy the ingestion-time enrichment step matches against
 * (packages/backend/src/dlt/enrichment.py's BUILTIN_FEEDBACK_TAXONOMY),
 * kept in sync by hand: one is the Python row-tagging dictionary, this is
 * the SQL side that counts what it tagged. `computed_theme_tags` is a
 * comma-joined string (a row can carry more than one theme), so a per-theme
 * count is a LIKE match rather than an equality check — approximate but
 * consistent with how sentimentCountSql/priorityCountSql already work here.
 */
const THEME_CATEGORIES = [
    'Billing',
    'Bug',
    'Support',
    'Onboarding',
    'Performance',
];
const themeCountSql = (theme) => `CASE WHEN \${col.computed_theme_tags} LIKE '%${theme}%' THEN 1 ELSE 0 END`;
/**
 * Curated semantic model for a customer feedback table landed by a support or
 * survey connector (Zendesk, Intercom, Freshdesk, SurveyMonkey).
 *
 * Sentiment and rating capabilities are optional:
 *
 * - `rating_sentiment` is derived in SQL when rating and scale columns exist.
 * - sentiment metrics count a source-provided `sentiment` column when present.
 *
 * Missing optional columns are reported by the pack generator and their
 * dependent fields are omitted from Explore.
 *
 * Period-over-period trend is not a pack field: `submitted_at` is a real date
 * dimension, so Explore compares periods natively.
 */
export const customerFeedbackPack = {
    id: 'customer_feedback',
    name: 'Customer feedback',
    description: 'Feedback volume and priority, plus sentiment and rating metrics when the landed table provides those fields.',
    // Every landed row is feedback that was actually submitted, so there is no
    // closed/void population to exclude the way a loan book has.
    populationFilter: null,
    /** One row per piece of feedback. */
    grain: ['feedback_id'],
    columns: [
        {
            key: 'customer_ref',
            synonyms: [
                'customer_ref',
                'customer_id',
                'user_id',
                'requester_id',
                'contact_id',
                'author_id',
                'submitter_id',
            ],
            type: DimensionType.STRING,
            required: false,
            // Optional: most feedback sources are anonymous. Present only so an
            // operator can join feedback to another pack about the same person.
            opaqueIdentifier: true,
            description: 'Who submitted the feedback, for joining to other sources about the same customer.',
        },
        {
            key: 'feedback_id',
            synonyms: [
                'feedback_id',
                // What Zendesk, Intercom, Freshdesk and SurveyMonkey actually
                // land — every one of their manifest entries declares
                // `primary_key: 'id'`, so without this the pack could never
                // match a real sync, only a hand-typed test fixture.
                'id',
                'ticket_id',
                'conversation_id',
                'response_id',
                'review_id',
                'survey_response_id',
                'message_id',
            ],
            type: DimensionType.STRING,
            required: true,
            // Only ever COUNT(DISTINCT …)'d, never compared to a literal, so a
            // numeric id is as usable as a string one. GitLab and Zendesk land
            // numbers here; without this the pack cannot match them at all.
            opaqueIdentifier: true,
            description: 'Feedback record identifier from the source system.',
        },
        {
            key: 'submitted_at',
            synonyms: [
                'submitted_at',
                'created_at',
                'submitted_date',
                'received_at',
                'responded_at',
                'feedback_date',
                'review_date',
            ],
            type: DimensionType.TIMESTAMP,
            required: true,
            description: 'When the feedback was submitted.',
        },
        {
            key: 'sentiment',
            synonyms: [
                'sentiment',
                'sentiment_label',
                'feedback_sentiment',
                'polarity',
            ],
            type: DimensionType.STRING,
            required: false,
            description: `Optional source-provided sentiment label, one of ${Object.values(FeedbackSentiment).join(', ')}.`,
        },
        {
            key: 'rating',
            synonyms: [
                'rating',
                'score',
                'nps_score',
                'csat_score',
                'satisfaction_score',
                'stars',
                'star_rating',
            ],
            type: DimensionType.NUMBER,
            required: false,
            description: 'Rating the respondent gave, on its own scale.',
        },
        {
            key: 'rating_scale_min',
            synonyms: [
                'rating_scale_min',
                'scale_min',
                'rating_min',
                'score_min',
            ],
            type: DimensionType.NUMBER,
            required: false,
            description: 'Inclusive lower bound of the rating scale.',
        },
        {
            key: 'rating_scale_max',
            synonyms: [
                'rating_scale_max',
                'scale_max',
                'rating_max',
                'score_max',
            ],
            type: DimensionType.NUMBER,
            required: false,
            description: 'Inclusive upper bound of the rating scale.',
        },
        {
            key: 'feedback_text',
            synonyms: [
                'feedback_text',
                'text',
                'comment',
                'body',
                'description',
                'verbatim',
                'message',
            ],
            type: DimensionType.STRING,
            required: false,
            description: 'Free-text feedback as submitted.',
        },
        {
            key: 'source',
            synonyms: ['source', 'channel', 'origin', 'via', 'platform'],
            type: DimensionType.STRING,
            required: false,
            description: 'Where the feedback came from, e.g. nps-survey.',
        },
        {
            key: 'tag',
            synonyms: ['tag', 'tags', 'label', 'labels', 'topic', 'category'],
            type: DimensionType.STRING,
            required: false,
            description: 'Source-supplied label, for grouping by topic.',
        },
        {
            key: 'priority',
            synonyms: ['priority', 'urgency', 'severity', 'priority_level'],
            type: DimensionType.STRING,
            required: false,
            description: 'Ticket priority as landed by the source, e.g. low, normal, high, urgent (Zendesk-style).',
        },
        {
            key: 'computed_sentiment_score',
            synonyms: ['computed_sentiment_score'],
            type: DimensionType.NUMBER,
            required: false,
            description: 'VADER compound sentiment score (-1..1) computed from feedback_text at ingestion time. Present only when the ingestion pipeline ran text enrichment.',
        },
        {
            key: 'computed_sentiment_label',
            synonyms: ['computed_sentiment_label'],
            type: DimensionType.STRING,
            required: false,
            description: `VADER-derived sentiment label computed from feedback_text at ingestion time, one of ${Object.values(FeedbackSentiment).join(', ')}. Present only when the ingestion pipeline ran text enrichment.`,
        },
        {
            key: 'computed_theme_tags',
            synonyms: ['computed_theme_tags'],
            type: DimensionType.STRING,
            required: false,
            description: 'Comma-joined rule-based theme tags matched against feedback_text at ingestion time. Present only when the ingestion pipeline ran text enrichment.',
        },
        {
            key: 'computed_theme_confidence',
            synonyms: ['computed_theme_confidence'],
            type: DimensionType.NUMBER,
            required: false,
            description: 'Keyword-coverage confidence (0..1) of the strongest theme matched at ingestion time: how much of that theme vocabulary the text used. Not a probability. Absent on tables imported before theme scoring landed.',
        },
        {
            key: 'computed_theme',
            synonyms: ['computed_theme'],
            type: DimensionType.STRING,
            required: false,
            description: 'Theme assigned by semantic grouping at ingestion time: feedback clustered by meaning and named by the configured AI provider. Present only when semantic grouping was enabled for the import.',
        },
        {
            key: 'computed_subtheme',
            synonyms: ['computed_subtheme'],
            type: DimensionType.STRING,
            required: false,
            description: 'Sub-theme within computed_theme, from the same semantic grouping run. Present only when semantic grouping was enabled for the import.',
        },
    ],
    dimensions: [
        {
            name: 'rating_position',
            label: 'Rating position',
            description: "The rating's position within its own scale, so ratings collected on different scales compare directly.",
            type: DimensionType.NUMBER,
            sql: RATING_POSITION,
        },
        {
            name: 'rating_sentiment',
            label: 'Rating sentiment',
            description: `Sentiment derived from the rating: positive at or above ${POSITIVE_POSITION_THRESHOLD} of scale, negative at or below ${NEGATIVE_POSITION_THRESHOLD}.`,
            type: DimensionType.STRING,
            sql: `CASE
    WHEN \${col.rating} IS NULL THEN NULL
    WHEN ${RATING_POSITION} >= ${POSITIVE_POSITION_THRESHOLD} THEN '${FeedbackSentiment.POSITIVE}'
    WHEN ${RATING_POSITION} <= ${NEGATIVE_POSITION_THRESHOLD} THEN '${FeedbackSentiment.NEGATIVE}'
    ELSE '${FeedbackSentiment.NEUTRAL}'
END`,
        },
    ],
    metrics: [
        {
            name: 'feedback_count',
            label: 'Feedback count',
            description: 'Number of distinct feedback records.',
            type: MetricType.COUNT_DISTINCT,
            sql: '${col.feedback_id}',
            format: null,
            round: null,
        },
        {
            name: 'rated_count',
            label: 'Rated feedback',
            description: 'Feedback records that carried a rating.',
            type: MetricType.SUM,
            sql: 'CASE WHEN ${col.rating} IS NOT NULL THEN 1 ELSE 0 END',
            format: null,
            round: null,
        },
        {
            name: 'positive_count',
            label: 'Positive feedback',
            description: 'Feedback records labelled positive.',
            type: MetricType.SUM,
            sql: sentimentCountSql(FeedbackSentiment.POSITIVE),
            format: null,
            round: null,
        },
        {
            name: 'neutral_count',
            label: 'Neutral feedback',
            description: 'Feedback records labelled neutral.',
            type: MetricType.SUM,
            sql: sentimentCountSql(FeedbackSentiment.NEUTRAL),
            format: null,
            round: null,
        },
        {
            name: 'negative_count',
            label: 'Negative feedback',
            description: 'Feedback records labelled negative.',
            type: MetricType.SUM,
            sql: sentimentCountSql(FeedbackSentiment.NEGATIVE),
            format: null,
            round: null,
        },
        {
            name: 'high_priority_count',
            label: 'High priority feedback',
            description: "Feedback records landed as 'high' or 'urgent' priority (Zendesk's own vocabulary; other sources vary).",
            type: MetricType.SUM,
            sql: `${priorityCountSql('high')} + ${priorityCountSql('urgent')}`,
            format: null,
            round: null,
        },
        {
            name: 'positive_share',
            label: 'Positive share',
            description: 'Share of feedback records labelled positive.',
            type: MetricType.NUMBER,
            sql: '${positive_count} / NULLIF(${feedback_count}, 0)',
            format: Format.PERCENT,
            round: 2,
        },
        {
            name: 'neutral_share',
            label: 'Neutral share',
            description: 'Share of feedback records labelled neutral.',
            type: MetricType.NUMBER,
            sql: '${neutral_count} / NULLIF(${feedback_count}, 0)',
            format: Format.PERCENT,
            round: 2,
        },
        {
            name: 'negative_share',
            label: 'Negative share',
            description: 'Share of feedback records labelled negative.',
            type: MetricType.NUMBER,
            sql: '${negative_count} / NULLIF(${feedback_count}, 0)',
            format: Format.PERCENT,
            round: 2,
        },
        {
            name: 'net_sentiment_score',
            label: 'Net sentiment score',
            description: 'Positive minus negative as a share of all feedback, on -100..100. With ratings behind the labels this is NPS: promoters minus detractors.',
            type: MetricType.NUMBER,
            sql: `(\${positive_count} - \${negative_count}) / NULLIF(\${feedback_count}, 0) * ${NET_SENTIMENT_SCALE}`,
            format: null,
            round: 0,
        },
        {
            name: 'average_rating',
            label: 'Average rating',
            description: 'Mean rating on its raw scale. Only comparable across sources collected on the same scale.',
            type: MetricType.AVERAGE,
            sql: '${col.rating}',
            format: null,
            round: 2,
        },
        {
            name: 'average_rating_position',
            label: 'Average rating position',
            description: 'Mean rating position within scale, so mixed scales compare safely.',
            type: MetricType.AVERAGE,
            sql: RATING_POSITION,
            format: Format.PERCENT,
            round: 2,
        },
        {
            name: 'average_computed_sentiment_score',
            label: 'Average computed sentiment score',
            description: 'Mean VADER compound sentiment score (-1..1) computed from feedback_text at ingestion time.',
            type: MetricType.AVERAGE,
            sql: '${col.computed_sentiment_score}',
            format: null,
            round: 2,
        },
        {
            name: 'average_theme_confidence',
            label: 'Average theme confidence',
            description: 'Mean keyword-coverage confidence of the strongest theme matched per record. AVG ignores NULLs, so unclassified records are excluded rather than counted as zero.',
            type: MetricType.AVERAGE,
            sql: '${col.computed_theme_confidence}',
            format: null,
            round: 2,
        },
        {
            name: 'classified_count',
            label: 'Classified feedback',
            description: 'Records that matched at least one built-in theme rule. Counts rows, so a record carrying several themes counts once — unlike summing the per-theme metrics.',
            type: MetricType.SUM,
            sql: 'CASE WHEN ${col.computed_theme_tags} IS NOT NULL THEN 1 ELSE 0 END',
            format: null,
            round: null,
        },
        ...THEME_CATEGORIES.map((theme) => ({
            name: `${theme.toLowerCase()}_feedback_count`,
            label: `${theme} feedback count`,
            description: `Feedback records tagged '${theme}' by rule-based theme matching against feedback_text at ingestion time.`,
            type: MetricType.SUM,
            sql: themeCountSql(theme),
            format: null,
            round: null,
        })),
    ],
};
//# sourceMappingURL=customerFeedback.js.map